import { memo, useContext, useState } from "react";
import PropTypes from "prop-types";
import { quotationActivityShape } from "app/utils/propTypes";
import { FormattedMessage } from "react-intl";
import ProgressiveImg from "app/pages/.shared/ProgressiveImg";
import { Amount } from "app/components/ui/Amount/Amount";
import get from "lodash/get";

import "./QuotationActivityHeader.scss";
import Badge from "app/pages/.shared/Badge";
import { useAnalytics } from "app/utils/analytics/useAnalytics";
import { Button } from "app/components/ui/Button/Button";
import Typography, { TYPOGRAPHY_VARIANTS } from "app/pages/.shared/Typography/Typography";
import LineClampViewMore from "app/pages/.shared/LineClampViewMore/LineClampViewMore";
import AppGlobalsContext from "app/AppGlobalsContext";
import { RESOLUTION } from "app/pages/.shared/responsive/responsiveReducer";
import { LessIcon, MoreIcon } from "app/icons/navigation";

const IMG_SIZE = {
	small: { width: 200, height: 100 },
	medium: { width: 200, height: 100 },
	large: { width: 225, height: 130 },
	xlarge: { width: 225, height: 130 },
};

const QuotationActivityHeader = ({ activity, total, minGuestPrice, hasIncludedGuest }) => {
	const { track } = useAnalytics();

	const { resolution } = useContext(AppGlobalsContext);
	const isMobile = resolution === RESOLUTION.SMALL || resolution === RESOLUTION.MEDIUM;
	const [isDesktopExpanded, setIsDesktopExpanded] = useState(false);

	const photoUrl = get(activity, "photo.url");
	const photoAlt = get(activity, "photo.alt");
	const photoThumbnail = get(activity, "photo.thumbnail");
	const hasParagraphs = activity?.paragraphs?.length > 0;
	const formattedParagraphs = activity?.paragraphs?.includes('class="paragraphs"')
		? activity?.paragraphs
		: `<section class="paragraphs"><article class="paragraphs__item">${
				activity?.paragraphs
			}</article></section>`;
	const renderDescription = () => (
		<div
			dangerouslySetInnerHTML={{
				__html: formattedParagraphs,
			}}
			className="quotation-activity__paragraphs"
		/>
	);

	const toggleDesktopContent = () => {
		track(isDesktopExpanded ? "activity_less_details" : "activity_more_details");
		setIsDesktopExpanded(prev => !prev);
	};

	let priceNode = (
		<div className="quotation-activity__starting-price">
			<FormattedMessage
				id="quotation.activity.guest.price.label.1"
				values={{
					price: <Amount value={minGuestPrice} />,
				}}
			/>
		</div>
	);

	if (hasIncludedGuest && total === 0) {
		priceNode = (
			<div className="quotation-activity__starting-price">
				<Badge>
					<FormattedMessage id="general.included" />
				</Badge>
			</div>
		);
	} else if (total > 0) {
		priceNode = (
			<div className="quotation-activity__total">
				<Amount sign="+" signIsBold={false} value={total} size="large" />
			</div>
		);
	}

	return (
		<div className="quotation-activity__header">
			{priceNode}
			<div className="quotation-activity__header-content">
				{photoUrl && (
					<div className="quotation-activity__photo">
						<ProgressiveImg
							sizes={IMG_SIZE}
							src={photoUrl}
							alt={photoAlt}
							thumbnail={photoThumbnail}
						/>
					</div>
				)}
				<div className="quotation-activity__header-text">
					<div className="quotation-activity__details">
						<Typography
							variant={TYPOGRAPHY_VARIANTS.LARGE}
							isBold
							className="quotation-activity__label"
						>
							{activity.name}
						</Typography>
					</div>

					{!isMobile && hasParagraphs && !isDesktopExpanded && (
						<div className="quotation-activity__excerpt">
							<LineClampViewMore
								enableShowMore={false}
								lineCount={4}
								text={renderDescription()}
							/>
						</div>
					)}
				</div>

				{hasParagraphs && (
					<div className="quotation-activity__content">
						{isMobile ? (
							<LineClampViewMore
								enableShowMore={true}
								lineCount={2}
								text={renderDescription()}
								customShowMoreComponent={
									<div data-testid="read-more-button">
										<Button
											variant="borderless"
											startIcon={<MoreIcon />}
											onClick={() => track("activity_more_details")}
										>
											<FormattedMessage id="product.page.more.details" />
										</Button>
									</div>
								}
								customShowLessComponent={
									<div data-testid="read-more-button">
										<Button
											variant="borderless"
											startIcon={<LessIcon />}
											onClick={() => track("activity_less_details")}
										>
											<FormattedMessage id="product.page.less.details" />
										</Button>
									</div>
								}
							/>
						) : (
							<>
								{isDesktopExpanded && renderDescription()}

								<div className="quotation-activity__details-button">
									<Button
										variant="borderless"
										startIcon={isDesktopExpanded ? <LessIcon /> : <MoreIcon />}
										onClick={toggleDesktopContent}
									>
										{isDesktopExpanded ? (
											<FormattedMessage id="product.page.less.details" />
										) : (
											<FormattedMessage id="product.page.more.details" />
										)}
									</Button>
								</div>
							</>
						)}
					</div>
				)}
			</div>
		</div>
	);
};

QuotationActivityHeader.propTypes = {
	activity: quotationActivityShape,
	total: PropTypes.number,
	minGuestPrice: PropTypes.number,
	hasIncludedGuest: PropTypes.bool,
};

QuotationActivityHeader.defaultProps = {
	activity: {},
};

export default memo(QuotationActivityHeader);
